import { useMemo } from 'react'

import { useModals, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import AddCampaignComparableModal from '@modals/AddCampaignComparableModal'
import BrowseCampaignComparablesModal from '@modals/BrowseCampaignComparablesModal'
import CreateOrEditCampaignComparableModal from '@modals/CreateOrEditCampaignComparableModal'
import SearchCampaignComparablesModal from '@modals/SearchCampaignComparablesModal'

import handleCallbackAction, { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import { CampaignComparableModel } from '@models/types'

import PageContent from './components/PageContent'

export type PageContextPayload = {
  callbacks: {
    showAddCampaignComparableModal: () => void,
    showCreateOrEditCampaignComparableModal: () => void,
    showSearchCampaignComparablesModal: () => void,
  },
}

const defaultState = {
  showAddCampaignComparableModal: false,
  showBrowseCampaignComparablesModal: false,
  showCreateOrEditCampaignComparableModal: false,
  showSearchCampaignComparablesModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    AddCampaignComparableModal: {
      closeModal: () => setState({ showAddCampaignComparableModal: false }),
    },
    BrowseCampaignComparablesModal: {
      closeModal: () => setState({ showBrowseCampaignComparablesModal: false }),
      createCampaignComparable: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      resetCampaignDataProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCampaignDataProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCampaignComparableModal: {
      closeModal: () => setState({ showCreateOrEditCampaignComparableModal: false }),
      createCampaignComparable: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCampaignComparable: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),

    },
    SearchCampaignComparablesModal: {
      closeModal: () => setState({ showSearchCampaignComparablesModal: false }),
      createComparableFromDataProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showAddCampaignComparableModal,
    showBrowseCampaignComparablesModal,
    showCreateOrEditCampaignComparableModal,
    showSearchCampaignComparablesModal,
  } = state

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
    topModalKey,
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showAddCampaignComparableModal: (payload: { campaignComparable: CampaignComparableModel }) => {
        setModalData('AddCampaignComparableModal', payload)
        setState({ showAddCampaignComparableModal: true })
      },
      showBrowseCampaignComparablesModal: (payload: { campaignComparable: CampaignComparableModel }) => {
        setModalData('BrowseCampaignComparablesModal', payload)
        setState({ showBrowseCampaignComparablesModal: true })
      },
      showCreateOrEditCampaignComparableModal: (payload: { campaignComparable: CampaignComparableModel }) => {
        setModalData('CreateOrEditCampaignComparableModal', payload)
        setState({ showCreateOrEditCampaignComparableModal: true })
      },
      showSearchCampaignComparablesModal: (payload: { campaignComparable: CampaignComparableModel }) => {
        setModalData('SearchCampaignComparablesModal', payload)
        setState({ showSearchCampaignComparablesModal: true })
      },
    },
  }), [topModalKey])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent />

        <AddCampaignComparableModal
          callbacks={callbacks('AddCampaignComparableModal', setState)}
          showModal={showAddCampaignComparableModal}
        />

        <BrowseCampaignComparablesModal
          // @ts-expect-error TS2322
          callbacks={callbacks('BrowseCampaignComparablesModal', setState)}
          showModal={showBrowseCampaignComparablesModal}
        />

        <CreateOrEditCampaignComparableModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCampaignComparableModal', setState)}
          showModal={showCreateOrEditCampaignComparableModal}
        />

        <SearchCampaignComparablesModal
          // @ts-expect-error TS2322
          callbacks={callbacks('SearchCampaignComparablesModal', setState)}
          showModal={showSearchCampaignComparablesModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
